import * as R from 'remeda';
import { DatasetDataPatchAPISchema } from '@rossum/mdh-api-client';
import { DatasetColumn, ElisColumnType } from '../types';
import { Row, RowEdits } from './types';

// A cell is "cleared" when the grid editor empties it. The string editor yields
// `undefined`; the numeric editor's valueParser yields `null`
// (`value === '' ? null : Number(value)`), so both must be treated as cleared.
const isClearedValue = (value: unknown): boolean =>
  value === undefined || value === null;

// Resolve the value a cleared cell should take. A nullable column becomes
// `null`; a non-nullable column can't be emptied, so it keeps the original
// value (the clear is rejected for that field rather than producing a payload
// the schema would reject).
const resolveClearedValue = (
  key: string,
  original: Row,
  nullableColumns: ReadonlySet<string>
): unknown => (nullableColumns.has(key) ? null : original[key]);

// Given the row the grid just committed, the pristine server row, and the
// row's existing pending changes, return the next set of changes (field -> new
// value) containing only cells that actually differ from the original. An empty
// result means the row is back to its original state and should drop its edit.
//
// Only the dataset's declared editable columns are considered, so the row's
// own bookkeeping (the ROW_STATUS symbol) and server-internal fields
// (`_id`/`__digest_md5`) are excluded by construction — we never need to
// denylist keys on our side.
export const resolveCellEdits = (
  newRow: Row,
  original: Row,
  existingChanges: Record<string, unknown>,
  nullableColumns: ReadonlySet<string>,
  editableColumns: ReadonlySet<string>
): Record<string, unknown> =>
  [...editableColumns]
    // Only diff columns the committed row actually carries — an editable column
    // absent from the row isn't an emptied cell, just not present.
    .filter(key => key in newRow)
    .reduce<Record<string, unknown>>(
      (changes, key) => {
        const value = isClearedValue(newRow[key])
          ? resolveClearedValue(key, original, nullableColumns)
          : newRow[key];

        // Reverting a cell to its original value drops it from the changes;
        // any genuinely different value is recorded.
        return R.isDeepEqual(value, original[key])
          ? R.omit(changes, [key])
          : { ...changes, [key]: value };
      },
      { ...existingChanges }
    );

const currentDateValue = (): string =>
  new Date().toISOString().replace(/T.*/, 'T00:00:00+00:00');

const defaultValueByType = (): Record<ElisColumnType, unknown> => ({
  string: '',
  number: 0,
  bool: false,
  date: currentDateValue(),
  object: {},
  array: [],
  unknown: '',
});

export const buildNewRowDefaults = (
  columns: DatasetColumn[]
): Record<string, unknown> => {
  const defaults = defaultValueByType();
  return Object.fromEntries(
    columns.map(column => [
      column.datasetAttributeName,
      column.nullable ? null : defaults[column.type],
    ])
  );
};

// Copy the declared column values from an existing row to seed a duplicated
// row. Only the dataset's columns are carried over, so the row's own
// bookkeeping (the ROW_STATUS symbol) and server-internal fields
// (`_id`/`__digest_md5`) are excluded by construction — the same principle as
// `resolveCellEdits`.
export const buildDuplicatedRowChanges = (
  columns: DatasetColumn[],
  row: Row
): Record<string, unknown> =>
  Object.fromEntries(
    columns.map(column => [
      column.datasetAttributeName,
      row[column.datasetAttributeName],
    ])
  );

export const buildPatchOperations = (
  edits: RowEdits
): DatasetDataPatchAPISchema['operations'] =>
  [...edits].flatMap(([id, edit]): DatasetDataPatchAPISchema['operations'] => {
    if (edit.kind === 'deleted') return [{ op: 'remove', id }];
    if (edit.kind === 'new') return [{ op: 'add', value: edit.changes }];

    return R.isEmpty(edit.changes)
      ? []
      : [{ op: 'replace', id, value: edit.changes }];
  });
